import React from "react";
import Table from "components/Table";
import { useTranslation } from "react-i18next";
import { IconDownload, IconTrash } from "@tabler/icons-react";
import { ColumnDef } from "@tanstack/react-table";
import moment from "moment";
import clsx from "clsx";
import { FileProps } from ".";
import LoadingIcon from "components/LoadingIcon";
import Tooltip from "components/Tooltip";

interface FilesProps {
  file_id: string;
  file_name: string;
  file: File | null;
  creation_date: string;
  url: string;
  uploaded: boolean;
}

const FilesTable = ({
  files,
  uploadedFiles,
  onRemoveFile,
  onRemoveUploadedFile,
}: {
  files?: File[];
  uploadedFiles?: FileProps[];
  onRemoveFile?: (file: File) => void;
  onRemoveUploadedFile?: (id: string) => void;
}) => {
  const { t, i18n } = useTranslation();
  const isEng = i18n.language === "en";
  const [data, setData] = React.useState<FilesProps[]>([]);

  React.useEffect(() => {
    const uploaded = uploadedFiles?.map((file) => ({
      file_id: file.file_id,
      file_name: file.file_name,
      file: null,
      creation_date: file.created
        ? moment(file.created).format(
            isEng ? "DD.MM.YYYY, hh:mm A" : "DD.MM.YYYY, HH:mm"
          )
        : "",
      url: file.url,
      uploaded: true,
    }));

    const selected =
      files?.map((file) => ({
        file_id: "",
        file_name: file.name,
        file: file,
        creation_date: "",
        url: "",
        uploaded: false,
      })) ?? [];

    setData(uploaded?.length ? [...uploaded, ...selected] : selected);
  }, [files?.length, uploadedFiles?.length]);

  const getColumns = () =>
    React.useMemo<ColumnDef<FilesProps>[]>(
      () => [
        {
          accessorFn: (row: FilesProps) => row.file_name,
          id: "file_name",
          cell: (info) => (
            <div className="whitespace-nowrap flex">
              <Tooltip truncate content={info.row.original.file_name}>
                {info.row.original.file_name}
              </Tooltip>
            </div>
          ),
          header: () => <div>{t("commons.document")}</div>,
          enableColumnFilter: false,
          enableSorting: false,
        },
        {
          accessorFn: (row: FilesProps) => row.creation_date,
          id: "creation_date",
          header: () => <div>{t("commons.uploadedOn")}</div>,
          cell: (info) => (
            <div>
              {isEng
                ? info.row.original.creation_date ?? ""
                : info.row.original.creation_date
                ? `${info.row.original.creation_date} Uhr`
                : ""}
            </div>
          ),
          enableColumnFilter: false,
          enableSorting: true,
          maxSize: 100,
        },
        {
          id: "icons",
          header: () => <div></div>,
          cell: (info) => (
            <div className="flex max-w-[60px] justify-center">
              <IconDownload
                className={clsx("text-indigo-700 cursor-pointer w-5 h-5", {
                  "pointer-events-none cursor-not-allowed opacity-40":
                    !info.row.original.uploaded,
                })}
                stroke={2.5}
                onClick={() =>
                  info.row.original.url
                    ? window.open(info.row.original.url, "_blank")
                    : null
                }
              />
              {onRemoveFile && (
                <IconTrash
                  className="text-red-700 ml-2 cursor-pointer w-5 h-5"
                  stroke={2.5}
                  onClick={() =>
                    info.row.original.file
                      ? onRemoveFile?.(info.row.original.file)
                      : onRemoveUploadedFile?.(info.row.original.file_id)
                  }
                />
              )}
            </div>
          ),
          enableColumnFilter: false,
          enableSorting: false,
          maxSize: onRemoveFile ? 30 : 10,
        },
      ],
      [files, uploadedFiles]
    );

  return (
    <Table
      getColumns={getColumns as () => ColumnDef<unknown, unknown>[]}
      data={data}
      borderedStyle
      headerContainerStyle="!bg-white"
      headerDivStyle="!py-1 h-[24px] !px-3 !text-sm font-semibold text-gray-900 border-r"
      customColumnStyle={clsx(
        "border-r h-[24px] last-of-type:border-r-0 border-gray-300 !py-1",
        "first-of-type:pl-4 last-of-type:pr-4 text-xs font-medium text-gray-900 !px-3"
      )}
      customRowStyle="h-[24px]"
    />
  );
};

export default FilesTable;
